import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { styled } from '@mui/material/styles';

import PrivateRoute from './Components/Security/PrivateRoute';
import RoleGuard from './Components/Security/RoleGuard';
import Landing from './Components/Layout/Landing';
import NotFound from './Components/Layout/NotFound';
import WebScrapper from './Components/Project/WebScraper/WebScrapper';
import IHubAdapter from './Components/Project/IHubAdapter/IHubAdapter';
import UserManagement from './Components/Project/UserManagement/UserManagement';
import ServicesMonitor from './Components/Project/ServicesMonitor/ServicesMonitor';
import SmartScanningProxy from './Components/Project/SmartScanningProxy/SmartScanningProxy';
import BatchID from './Components/Project/BatchID/BatchID';
import Bugnion from './Components/Project/Bugnion/Bugnion';

import { Sidebar } from './layout/Sidebar';
import { Topbar } from './layout/Topbar';
import { AppFooter } from './layout/Footer';
import { useAuth } from './Context/AuthenticationContext';
import authService from './Services/AuthService';
import { SHOW_BUGNION, SHOW_SERVICES_MONITOR, SHOW_SMSC_CLIENT_CONFIG } from './config';

import './App.scss';

const AppShell = styled('div')({
  display: 'flex',
  height: '100vh',
  overflow: 'hidden',
});

const MainArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
});

const Content = styled('main')({
  flex: 1,
  padding: '12px 24px 12px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
});

function App() {
  const { login, isUserAuthenticated } = useAuth();
  const isDashboardRoute = window.location.pathname === '/dashboard';
  const isAuthenticated = isUserAuthenticated();
  const isPublicDashboard = isDashboardRoute && !isAuthenticated;
  const showShell = isAuthenticated;

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const isPublicPath = window.location.pathname === '/dashboard';
      const justLoggedOut = authService.isJustLoggedOut?.() === true;
      const auth = await authService.isUserAuthenticated();
      const isAnon = !auth || Object.keys(auth).length === 0;
      if (!isPublicPath && !justLoggedOut && isAnon) {
        login();
      }
    })();
  }, [login]);

  if (isPublicDashboard) {
    return (
      <Router>
        <Routes>
          <Route path="/dashboard" element={<Landing />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <AppShell>
        {showShell && (
          <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        )}

        <MainArea>
          {showShell && <Topbar onMobileMenuOpen={() => setMobileOpen(true)} />}

          <Content>
            <Routes>
              <Route path="/dashboard" element={<Landing />} />

              <Route element={<PrivateRoute />}>
                <Route path="/" element={<Landing />} />
                <Route
                  path="/component_webscraper"
                  element={
                    <RoleGuard roles={['WEB_SCRAPER']} serviceLabel="Web Scraper">
                      <WebScrapper />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/ihub_adapter"
                  element={
                    <RoleGuard roles={['IHUB_CUSTOMER_MIGRATION']} serviceLabel="iHub Adapter">
                      <IHubAdapter />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/batchid"
                  element={
                    <RoleGuard
                      roles={['AMS_PAYMENTS', 'PRS_PAYMENTS', 'TM1_PAYMENTS']}
                      serviceLabel="Batch ID"
                    >
                      <BatchID />
                    </RoleGuard>
                  }
                />
                {SHOW_BUGNION && (
                  <Route
                    path="/bugnion"
                    element={
                      <RoleGuard roles={['BUGNION_INTERFACE']} serviceLabel="Bugnion">
                        <Bugnion />
                      </RoleGuard>
                    }
                  />
                )}
                <Route
                  path="/user_management"
                  element={
                    <RoleGuard roles={['USER_MANAGEMENT']} serviceLabel="User Management">
                      <UserManagement />
                    </RoleGuard>
                  }
                />
                {SHOW_SERVICES_MONITOR && (
                  <Route
                    path="/services_monitor"
                    element={
                      <RoleGuard roles={['ROLE_SERVICES_MONITOR']} serviceLabel="Services Monitor">
                        <ServicesMonitor />
                      </RoleGuard>
                    }
                  />
                )}
                {SHOW_SMSC_CLIENT_CONFIG && (
                  <Route
                    path="/twindolphin_onboarding"
                    element={
                      <RoleGuard roles={['ROLE_SMSC_ADMIN']} serviceLabel="SMSC Config">
                        <SmartScanningProxy />
                      </RoleGuard>
                    }
                  />
                )}
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Content>

          {showShell && <AppFooter />}
        </MainArea>
      </AppShell>
    </Router>
  );
}

export default App;
